/**
 * SalarySourceBadge — small chip that names the national statistics
 * body behind a uni's per-uni salary figure. Rendered under the
 * "Grad. salary" number on uni cards so families see the depth-of-
 * data at a glance.
 *
 * Absence of this badge = we don't have per-uni verified salary for
 * this university (we fall back to sector medians or tier-mapped
 * estimates). Presence = one of ~10 national statistical bodies
 * has published this uni's own graduate income.
 *
 * The chip colour maps to the source's country group so the badge
 * doubles as a country-of-source cue.
 */

import { Link } from "react-router-dom";

/**
 * @typedef {Object} SourceEntry
 * @property {string} short  Short label shown in the badge
 * @property {string} chip   Tailwind classes for background + border
 * @property {string} full   Full attribution shown in tooltip
 */

/** @type {Array<[RegExp, SourceEntry]>} */
const SOURCE_MAP = [
  [/\bHESA\b/i,        { short: "HESA",       chip: "bg-blue-50 text-blue-900 border-blue-200",         full: "HESA — UK Higher Education Statistics Agency" }],
  [/\bLEO\b/i,         { short: "LEO",        chip: "bg-blue-50 text-blue-900 border-blue-200",         full: "LEO — UK HMRC-linked Longitudinal Educational Outcomes" }],
  [/\bHEA\b/i,         { short: "HEA",        chip: "bg-emerald-50 text-emerald-900 border-emerald-200", full: "HEA — Ireland Higher Education Authority" }],
  [/\bQILT\b|SES /i,   { short: "QILT",       chip: "bg-amber-50 text-amber-900 border-amber-200",       full: "QILT — Australia's Quality Indicators for Learning & Teaching (SES + GOS)" }],
  [/Scorecard/i,       { short: "Scorecard",  chip: "bg-red-50 text-red-900 border-red-200",             full: "US College Scorecard — Dept of Education" }],
  [/\bNSSE\b/i,        { short: "NSSE",       chip: "bg-red-50 text-red-900 border-red-200",             full: "NSSE — Indiana Uni National Survey of Student Engagement" }],
  [/\b(StatCan|T1FF|NGS)\b/i, { short: "StatCan", chip: "bg-rose-50 text-rose-900 border-rose-200",     full: "Statistics Canada · T1FF tax records + National Graduates Survey" }],
  [/\bCBS\b/i,         { short: "CBS",        chip: "bg-orange-50 text-orange-900 border-orange-200",    full: "CBS Netherlands — StatLine graduate earnings" }],
  [/Céreq|Génération|Generation/i, { short: "Céreq", chip: "bg-indigo-50 text-indigo-900 border-indigo-200", full: "Céreq · Enquête Génération per-uni starting salary (France)" }],
  [/\bINSEE\b/i,       { short: "INSEE",      chip: "bg-indigo-50 text-indigo-900 border-indigo-200",    full: "INSEE — French national statistics" }],
  [/Destatis/i,        { short: "Destatis",   chip: "bg-yellow-50 text-yellow-900 border-yellow-200",    full: "Destatis — German Federal Statistical Office" }],
  [/AlmaLaurea/i,      { short: "AlmaLaurea", chip: "bg-teal-50 text-teal-900 border-teal-200",          full: "AlmaLaurea — Italian graduates occupational survey" }],
  [/\bINE\b/i,         { short: "INE",        chip: "bg-teal-50 text-teal-900 border-teal-200",          full: "INE — Instituto Nacional de Estadística (Spain)" }],
  [/\bDST\b/i,         { short: "DST",        chip: "bg-cyan-50 text-cyan-900 border-cyan-200",          full: "Danmarks Statistik — Danish graduate earnings" }],
  [/\bSCB\b/i,         { short: "SCB",        chip: "bg-cyan-50 text-cyan-900 border-cyan-200",          full: "SCB — Statistics Sweden graduate income" }],
  [/\bSSB\b/i,         { short: "SSB",        chip: "bg-cyan-50 text-cyan-900 border-cyan-200",          full: "SSB — Statistics Norway graduate income" }],
  [/Tilastokeskus/i,   { short: "Tilastok.",  chip: "bg-cyan-50 text-cyan-900 border-cyan-200",          full: "Tilastokeskus — Statistics Finland graduate employment + income" }],
  [/\bAISHE\b|\bNIRF\b/i, { short: "AISHE/NIRF", chip: "bg-orange-50 text-orange-900 border-orange-200", full: "AISHE + NIRF — India Ministry of Education per-institution placement medians" }],
  [/\bBFS\b|\bOFS\b|Absolventenstudien/i, { short: "BFS/OFS", chip: "bg-red-50 text-red-900 border-red-200", full: "BFS/OFS — Swiss Federal Statistical Office · Absolventenstudien der Hochschulen" }],
  [/\bMOHESR\b/i,      { short: "MOHESR",     chip: "bg-lime-50 text-lime-900 border-lime-200",          full: "MOHESR — UAE Ministry of Higher Education Graduate Employability Survey" }],
  [/Statistik Austria|Uni:data|Uni-Data/i, { short: "Statistik AT", chip: "bg-red-50 text-red-900 border-red-200", full: "Statistik Austria — Uni:data Absolvent:innen-Erhebung" }],
  [/\bDGES\b|\bOTES\b|InfoCursos/i, { short: "DGES/OTES", chip: "bg-emerald-50 text-emerald-900 border-emerald-200", full: "DGES OTES — Portugal Direção-Geral do Ensino Superior · Percursos dos Diplomados" }],
  [/\bKEDI\b|\bKESS\b/i, { short: "KEDI",     chip: "bg-blue-50 text-blue-900 border-blue-200",          full: "KEDI KESS — Korea Educational Development Institute · higher-ed graduate income survey" }],
  [/Statbel/i,         { short: "Statbel",    chip: "bg-indigo-50 text-indigo-900 border-indigo-200",    full: "Statbel — Belgium National Statistics · Hoger Onderwijs graduaten salarisstatistieken" }],
  [/\bMOM\b/,          { short: "MOM",        chip: "bg-red-50 text-red-900 border-red-200",              full: "MOM — Singapore Ministry of Manpower · Graduate Employment Survey" }],
  [/\bCensStatD\b|\bUGC\b/i, { short: "CensStatD", chip: "bg-rose-50 text-rose-900 border-rose-200",     full: "CensStatD / UGC — Hong Kong Census & Statistics Department + University Grants Committee Graduate Employment Survey" }],
  [/\bMEXT\b/i,        { short: "MEXT",       chip: "bg-slate-100 text-slate-900 border-slate-300",       full: "MEXT — Japan Ministry of Education, Culture, Sports, Science and Technology · Basic Survey of Schools" }],
];

/**
 * Map a raw salary_source string to a display entry, or null if the
 * source doesn't match any known publisher.
 */
function resolveSource(salarySource) {
  if (!salarySource) return null;
  for (const [re, entry] of SOURCE_MAP) {
    if (re.test(salarySource)) return entry;
  }
  return null;
}

/**
 * Return the freshness bucket for a given ISO-8601 ingestion timestamp
 * (as stored under `uni.outcomes.salary_ingested_at`).
 *
 *   • `< 12` months old   → { tone: "fresh", label: "Fresh" }
 *   • `12–36` months old  → null  (silent — the default state)
 *   • `> 36` months old   → { tone: "stale", label: "3 yrs+ old" }  (or the true year count)
 *
 * We stay discreet because most rows ARE recent — surfacing "Fresh"
 * everywhere devalues the signal. Only stale years get the amber tone.
 */
function resolveFreshness(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const months = (Date.now() - t) / (1000 * 60 * 60 * 24 * 30.4375);
  if (months < 12) return { tone: "fresh", label: "Fresh" };
  if (months < 36) return null;
  const yrs = Math.floor(months / 12);
  return { tone: "stale", label: `${yrs} yrs old` };
}

export default function SalarySourceBadge({ salarySource, salaryIngestedAt, size = "sm", className = "", link = true }) {
  const entry = resolveSource(salarySource);
  if (!entry) return null;
  const freshness = resolveFreshness(salaryIngestedAt);
  const sizeClasses = size === "xs"
    ? "text-[9px] px-1 py-0.5"
    : "text-[10px] px-1.5 py-0.5";
  const baseClass = `inline-flex items-center gap-1 rounded border font-underground-sc tracking-wider ${entry.chip} ${sizeClasses}`;
  const testId = `salary-source-badge-${entry.short.toLowerCase().replace(/\W+/g, "-")}`;
  const inner = (
    <>
      <span className="w-1 h-1 rounded-full bg-current opacity-70" />
      {entry.short}
    </>
  );
  const wrapClass = `inline-flex max-w-full flex-wrap items-center gap-1 ${className}`;
  const freshChip = freshness ? (
    <span
      data-testid={`salary-freshness-chip-${freshness.tone}`}
      title={
        freshness.tone === "fresh"
          ? `Ingested from the national statistics feed within the last 12 months — this is the freshest cohort available.`
          : `Latest per-uni salary WUA has ingested is ${freshness.label.toLowerCase()}. Waiting on the national body's next release.`
      }
      className={
        `inline-flex items-center gap-1 rounded border font-underground-sc tracking-wider ${sizeClasses} ` +
        (freshness.tone === "fresh"
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-amber-50 text-amber-900 border-amber-300")
      }
    >
      <span className="w-1 h-1 rounded-full bg-current opacity-80" />
      {freshness.label}
    </span>
  ) : null;
  if (link) {
    return (
      <span className={wrapClass}>
        <Link
          to="/methodology/sources"
          onClick={(e) => e.stopPropagation()}
          className={`${baseClass} hover:opacity-80 transition-opacity`}
          title={`${entry.full}\n\n${salarySource}\n\nClick to see every source we ingest from →`}
          data-testid={testId}
        >
          {inner}
        </Link>
        {freshChip}
      </span>
    );
  }
  return (
    <span className={wrapClass}>
      <span
        className={baseClass}
        title={`${entry.full}\n\n${salarySource}`}
        data-testid={testId}
      >
        {inner}
      </span>
      {freshChip}
    </span>
  );
}

export { resolveSource, resolveFreshness };
